import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useMe } from "@/auth/useMe";
import { AdminGate } from "@/components/AdminGate";
import { AuthGate } from "@/components/AuthGate";
import { Layout } from "@/components/Layout";
import { TabGate } from "@/components/TabGate";

// Tab key → route path, for redirecting home to the first tab the user can see.
const TAB_PATHS: Record<string, string> = {
  search: "/search",
  service_providers: "/service-providers",
  introduction_analyzer: "/introduction-analyzer",
  portco_analysis: "/portco-analysis",
  transcript_processor: "/transcript-processor",
  analytics: "/analytics",
  exclusions: "/exclusions",
  admin: "/admin",
};

// Each route is its own chunk. The four pages share dependencies (TanStack
// Query, lucide icons, shadcn primitives) but Rollup deduplicates them into
// shared chunks — net effect is the initial bundle drops to roughly the
// auth + layout + chosen-route's incremental cost.
const SearchPage = lazy(() =>
  import("@/routes/SearchPage").then((m) => ({ default: m.SearchPage })),
);
const QualDetailPage = lazy(() =>
  import("@/routes/QualDetailPage").then((m) => ({ default: m.QualDetailPage })),
);
const ServiceProvidersIndexPage = lazy(() =>
  import("@/routes/ServiceProvidersIndexPage").then((m) => ({ default: m.ServiceProvidersIndexPage })),
);
const ServiceProviderPage = lazy(() =>
  import("@/routes/ServiceProviderPage").then((m) => ({ default: m.ServiceProviderPage })),
);
const ExclusionsPage = lazy(() =>
  import("@/routes/ExclusionsPage").then((m) => ({ default: m.ExclusionsPage })),
);
const IntroductionAnalyzerPage = lazy(() =>
  import("@/routes/IntroductionAnalyzerPage").then((m) => ({
    default: m.IntroductionAnalyzerPage,
  })),
);
// AnalyticsPage pulls in recharts (~140kB raw); keep it in its own chunk so
// users who never visit /analytics don't pay the cost.
const AnalyticsPage = lazy(() =>
  import("@/routes/AnalyticsPage").then((m) => ({ default: m.AnalyticsPage })),
);
const PortcoNeedAnalysisPage = lazy(() =>
  import("@/routes/PortcoNeedAnalysisPage").then((m) => ({
    default: m.PortcoNeedAnalysisPage,
  })),
);
const TranscriptProcessorPage = lazy(() =>
  import("@/routes/TranscriptProcessorPage").then((m) => ({
    default: m.TranscriptProcessorPage,
  })),
);
const AdminPage = lazy(() =>
  import("@/routes/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const ScoutAnalyticsPage = lazy(() =>
  import("@/routes/ScoutAnalyticsPage").then((m) => ({ default: m.ScoutAnalyticsPage })),
);

function RouteFallback() {
  return <div className="text-muted-foreground">Loading…</div>;
}

/**
 * Home ("/") redirect. Sends the user to the first tab they can actually reach —
 * ``tabs`` from /api/me is in catalog order, so this prefers a listed-and-
 * accessible tab, then any accessible one. If nothing is accessible (rare), it
 * falls through to /search, whose ``TabGate`` renders the access-denied message.
 */
function HomeRedirect() {
  const { data, isLoading } = useMe();
  if (isLoading) return <RouteFallback />;
  const tabs = data?.tabs ?? {};
  const keys = Object.keys(tabs);
  const first =
    keys.find((k) => tabs[k].accessible && tabs[k].listed) ??
    keys.find((k) => tabs[k].accessible);
  return <Navigate to={(first && TAB_PATHS[first]) ?? "/search"} replace />;
}

export function App() {
  return (
    <AuthGate>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<Layout />}>
            {/* Home routes to the first tab the user can reach (Search for most,
                since it defaults to all_users). */}
            <Route path="/" element={<HomeRedirect />} />
            {/* Search is gated like every other tab; TabGate renders an inline
                access-denied message on denial (no redirect), so gating the
                fallback route can't loop. */}
            <Route path="/search" element={<TabGate tab="search"><SearchPage /></TabGate>} />
            {/* Detail page is admin-only — non-admin users see search
                results but can't drill into a single qual's full
                provenance. Not a nav tab, so it keeps ``AdminGate``. */}
            <Route
              path="/quals/:system/:qualId"
              element={<AdminGate><QualDetailPage /></AdminGate>}
            />
            {/* Every route below is gated by its tab's configured access mode
                (``tabs[key].accessible`` from /api/me) — see TabGate. */}
            <Route
              path="/service-providers"
              element={<TabGate tab="service_providers"><ServiceProvidersIndexPage /></TabGate>}
            />
            <Route
              path="/service-providers/:spId"
              element={<TabGate tab="service_providers"><ServiceProviderPage /></TabGate>}
            />
            <Route
              path="/analytics"
              element={<TabGate tab="analytics"><AnalyticsPage /></TabGate>}
            />
            <Route
              path="/scout-analytics"
              element={<TabGate tab="scout_analytics"><ScoutAnalyticsPage /></TabGate>}
            />
            <Route
              path="/exclusions"
              element={<TabGate tab="exclusions"><ExclusionsPage /></TabGate>}
            />
            <Route
              path="/introduction-analyzer"
              element={<TabGate tab="introduction_analyzer"><IntroductionAnalyzerPage /></TabGate>}
            />
            {/* Deep-link form: ``/introduction-analyzer/<introId>`` auto-fires
                the analysis on mount so the page is shareable / bookmarkable
                straight from a Salesforce id. The bare path still works for
                interactive paste-and-submit. */}
            <Route
              path="/introduction-analyzer/:introId"
              element={<TabGate tab="introduction_analyzer"><IntroductionAnalyzerPage /></TabGate>}
            />
            <Route
              path="/portco-analysis"
              element={<TabGate tab="portco_analysis"><PortcoNeedAnalysisPage /></TabGate>}
            />
            <Route
              path="/transcript-processor"
              element={<TabGate tab="transcript_processor"><TranscriptProcessorPage /></TabGate>}
            />
            <Route
              path="/admin"
              element={<TabGate tab="admin"><AdminPage /></TabGate>}
            />
          </Route>
        </Routes>
      </Suspense>
    </AuthGate>
  );
}
